import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
    inventoryFormTemplatesAPI,
    industrialProductsAPI,
    supplierProductsAPI,
    sitesAPI,
    openAuthPdf,
} from '@/lib/api';
import PrintButton from '@/components/PrintButton';
import { useAuth } from '@/context/AuthContext';
import { matchesSearch } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    FileText, Plus, Download, Pencil, Trash2, Copy, Search, ChevronDown, ChevronRight, X,
} from 'lucide-react';

const DEFAULT_ZONES = ['Reserve', 'Stand', 'Siege', 'Total'];

const emptyForm = () => ({
    id: null,
    name: '',
    site_id: '',
    zones: [...DEFAULT_ZONES],
    group_mode: 'category_family',
    items: [], // [{industrial_product_id, supplier_product_ids: []}]
});

export default function InventoryFormsPage() {
    const { hasPermission } = useAuth();
    const canManage = hasPermission('inventory_forms_manage');

    const [templates, setTemplates] = useState([]);
    const [sites, setSites] = useState([]);
    const [industrial, setIndustrial] = useState([]);
    const [supplierProducts, setSupplierProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(emptyForm());
    const [deleteTarget, setDeleteTarget] = useState(null);

    // Dialog filters
    const [search, setSearch] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterFamille, setFilterFamille] = useState('all');
    const [filterSupplier, setFilterSupplier] = useState('all');
    const [onlySelected, setOnlySelected] = useState(false);
    const [expanded, setExpanded] = useState({});

    useEffect(() => {
        loadAll();
    }, []);

    const loadAll = async () => {
        try {
            const [t, s, ip, sp] = await Promise.all([
                inventoryFormTemplatesAPI.getAll(),
                sitesAPI.getAll(),
                industrialProductsAPI.getAll(),
                supplierProductsAPI.getAll(),
            ]);
            setTemplates(t.data || []);
            setSites(s.data || []);
            setIndustrial(ip.data || []);
            setSupplierProducts(sp.data || []);
        } catch (e) {
            toast.error('Erreur chargement');
        } finally {
            setLoading(false);
        }
    };

    // ---- Selection helpers ----
    const getItem = (ipId) => form.items.find(it => it.industrial_product_id === ipId);

    const setItem = (ipId, updater) => {
        setForm(prev => {
            const existing = prev.items.find(it => it.industrial_product_id === ipId);
            const updated = updater(existing || { industrial_product_id: ipId, supplier_product_ids: [] });
            if (!updated || (updated.supplier_product_ids.length === 0 && !updated._keepRoot)) {
                // Keep the product (line without supplier) if _keepRoot
                return {
                    ...prev,
                    items: prev.items.filter(it => it.industrial_product_id !== ipId),
                };
            }
            const clean = { industrial_product_id: ipId, supplier_product_ids: updated.supplier_product_ids };
            const newItems = existing
                ? prev.items.map(it => it.industrial_product_id === ipId ? clean : it)
                : [...prev.items, clean];
            return { ...prev, items: newItems };
        });
    };

    const toggleIndustrial = (ip) => {
        const existing = getItem(ip.id);
        if (existing) {
            // Remove entirely
            setForm(prev => ({ ...prev, items: prev.items.filter(it => it.industrial_product_id !== ip.id) }));
        } else {
            // Add with no supplier selected = industrial-only row
            setForm(prev => ({ ...prev, items: [...prev.items, { industrial_product_id: ip.id, supplier_product_ids: [] }] }));
            setExpanded(e => ({ ...e, [ip.id]: true }));
        }
    };

    const toggleSupplier = (ipId, spId) => {
        setForm(prev => {
            const existing = prev.items.find(it => it.industrial_product_id === ipId);
            if (existing) {
                const has = existing.supplier_product_ids.includes(spId);
                const newIds = has
                    ? existing.supplier_product_ids.filter(x => x !== spId)
                    : [...existing.supplier_product_ids, spId];
                return {
                    ...prev,
                    items: prev.items.map(it =>
                        it.industrial_product_id === ipId
                            ? { ...it, supplier_product_ids: newIds }
                            : it
                    ),
                };
            }
            return {
                ...prev,
                items: [...prev.items, { industrial_product_id: ipId, supplier_product_ids: [spId] }],
            };
        });
    };

    const categories = useMemo(() =>
        [...new Set(industrial.map(p => p.category).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr')),
        [industrial]);
    const familles = useMemo(() =>
        [...new Set(industrial.map(p => p.famille).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr')),
        [industrial]);
    const suppliersList = useMemo(() => {
        const names = new Map();
        supplierProducts.forEach(sp => {
            if (sp.supplier_id && sp.supplier_name) names.set(sp.supplier_id, sp.supplier_name);
        });
        return Array.from(names, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'fr'));
    }, [supplierProducts]);

    const spByIndustrial = useMemo(() => {
        const m = new Map();
        supplierProducts.forEach(sp => {
            if (!m.has(sp.industrial_product_id)) m.set(sp.industrial_product_id, []);
            m.get(sp.industrial_product_id).push(sp);
        });
        return m;
    }, [supplierProducts]);

    // Filter industrial products for the picker
    const filteredIndustrial = useMemo(() => {
        const q = search.trim().toLowerCase();
        return industrial
            .filter(p => filterCategory === 'all' || p.category === filterCategory)
            .filter(p => filterFamille === 'all' || p.famille === filterFamille)
            .filter(p => {
                if (filterSupplier === 'all') return true;
                const sps = spByIndustrial.get(p.id) || [];
                return sps.some(sp => sp.supplier_id === filterSupplier);
            })
            .filter(p => !q || matchesSearch(p.name, q))
            .filter(p => !onlySelected || !!getItem(p.id))
            .sort((a, b) => {
                const catCmp = (a.category || '').localeCompare(b.category || '', 'fr');
                if (catCmp !== 0) return catCmp;
                const famCmp = (a.famille || '').localeCompare(b.famille || '', 'fr');
                if (famCmp !== 0) return famCmp;
                return a.name.localeCompare(b.name, 'fr');
            });
    }, [industrial, filterCategory, filterFamille, filterSupplier, search, onlySelected, form.items, spByIndustrial]);

    const totalSelectedSp = useMemo(
        () => form.items.reduce((sum, it) => sum + it.supplier_product_ids.length, 0),
        [form.items]
    );

    // Group filtered IP into Category > Family tree structure
    const groupedTree = useMemo(() => {
        const byCat = new Map();
        filteredIndustrial.forEach(ip => {
            const cat = ip.category || 'Sans catégorie';
            const fam = ip.famille || 'Sans famille';
            if (!byCat.has(cat)) byCat.set(cat, new Map());
            const fMap = byCat.get(cat);
            if (!fMap.has(fam)) fMap.set(fam, []);
            fMap.get(fam).push(ip);
        });
        return Array.from(byCat, ([cat, fMap]) => ({
            cat,
            families: Array.from(fMap, ([fam, prods]) => ({ fam, prods })).sort((a, b) => a.fam.localeCompare(b.fam, 'fr')),
        })).sort((a, b) => a.cat.localeCompare(b.cat, 'fr'));
    }, [filteredIndustrial]);

    // Helpers for category/family selection state
    const catSelectionState = (productsInCat) => {
        const selected = productsInCat.filter(p => getItem(p.id)).length;
        if (selected === 0) return 'none';
        if (selected === productsInCat.length) return 'all';
        return 'partial';
    };

    const toggleMultiple = (products, shouldSelect) => {
        setForm(prev => {
            const currentIds = new Set(prev.items.map(it => it.industrial_product_id));
            if (shouldSelect) {
                const newItems = [...prev.items];
                products.forEach(p => {
                    if (!currentIds.has(p.id)) {
                        newItems.push({ industrial_product_id: p.id, supplier_product_ids: [] });
                    }
                });
                return { ...prev, items: newItems };
            }
            // Remove all
            const ids = new Set(products.map(p => p.id));
            return { ...prev, items: prev.items.filter(it => !ids.has(it.industrial_product_id)) };
        });
    };

    // ---- Handlers ----
    const openCreate = () => {
        setForm(emptyForm());
        setSearch(''); setFilterCategory('all'); setFilterFamille('all'); setFilterSupplier('all'); setOnlySelected(false); setExpanded({});
        setDialogOpen(true);
    };

    const openEdit = (tpl) => {
        setForm({
            id: tpl.id,
            name: tpl.name || '',
            site_id: tpl.site_id || '',
            zones: tpl.zones?.length ? tpl.zones : [...DEFAULT_ZONES],
            group_mode: tpl.group_mode || 'category_family',
            items: (tpl.items || []).map(it => ({
                industrial_product_id: it.industrial_product_id,
                supplier_product_ids: it.supplier_product_ids || [],
            })),
        });
        setSearch(''); setFilterCategory('all'); setFilterFamille('all'); setFilterSupplier('all'); setOnlySelected(false);
        setExpanded(Object.fromEntries((tpl.items || []).map(it => [it.industrial_product_id, true])));
        setDialogOpen(true);
    };

    const duplicate = async (tpl) => {
        try {
            await inventoryFormTemplatesAPI.create({
                name: `${tpl.name} (copie)`,
                site_id: tpl.site_id || null,
                zones: tpl.zones,
                group_mode: tpl.group_mode,
                items: tpl.items,
            });
            toast.success('Modèle dupliqué');
            loadAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const save = async () => {
        if (!form.name.trim()) {
            toast.error('Le nom est obligatoire');
            return;
        }
        if (form.items.length === 0) {
            toast.error('Sélectionnez au moins un produit');
            return;
        }
        try {
            const payload = {
                name: form.name.trim(),
                site_id: form.site_id || null,
                zones: form.zones,
                group_mode: form.group_mode,
                items: form.items,
            };
            if (form.id) {
                await inventoryFormTemplatesAPI.update(form.id, payload);
                toast.success('Modèle mis à jour');
            } else {
                await inventoryFormTemplatesAPI.create(payload);
                toast.success('Modèle créé');
            }
            setDialogOpen(false);
            loadAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await inventoryFormTemplatesAPI.delete(deleteTarget.id);
            toast.success('Modèle supprimé');
            setDeleteTarget(null);
            loadAll();
        } catch (e) {
            toast.error('Erreur suppression');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="inventory-forms-page">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
                        <FileText className="w-7 h-7 text-amber-600" />
                        Fiches d'inventaire
                    </h1>
                    <p className="text-slate-500 mt-1 text-sm">
                        Créez des modèles imprimables personnalisés pour faciliter la saisie terrain.
                    </p>
                </div>
                {canManage && (
                    <Button onClick={openCreate} className="btn-primary" data-testid="new-template-btn">
                        <Plus className="w-4 h-4 mr-2" /> Nouveau modèle
                    </Button>
                )}
            </div>

            <FromMovementsCard sites={sites} />

            {templates.length === 0 ? (
                <Card>
                    <CardContent className="py-16 text-center">
                        <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p className="text-slate-500 mb-4">Aucun modèle de fiche d'inventaire.</p>
                        {canManage && (
                            <Button onClick={openCreate} className="btn-primary">
                                <Plus className="w-4 h-4 mr-2" /> Créer mon premier modèle
                            </Button>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {templates.map(tpl => {
                        const site = sites.find(s => s.id === tpl.site_id);
                        const spCount = (tpl.items || []).reduce((sum, it) => sum + (it.supplier_product_ids?.length || 0), 0);
                        return (
                            <Card key={tpl.id} className="hover:shadow-md transition-shadow" data-testid={`template-card-${tpl.id}`}>
                                <CardContent className="p-4 space-y-3">
                                    <div>
                                        <h3 className="font-semibold text-slate-900 truncate">{tpl.name}</h3>
                                        {site && <p className="text-xs text-slate-500">Site : {site.name}</p>}
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
                                            {tpl.items?.length || 0} produits industriels
                                        </span>
                                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                                            {spCount} fournisseurs
                                        </span>
                                        {tpl.zones?.map(z => (
                                            <span key={z} className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">{z}</span>
                                        ))}
                                    </div>
                                    <div className="flex gap-1.5 pt-2 border-t">
                                        <Button
                                            size="sm"
                                            className="flex-1 bg-amber-600 hover:bg-amber-700"
                                            onClick={() => openAuthPdf(inventoryFormTemplatesAPI.pdfUrl(tpl.id))}
                                            data-testid={`download-pdf-${tpl.id}`}
                                        >
                                            <Download className="w-4 h-4 mr-1" /> PDF
                                        </Button>
                                        <PrintButton testId={`print-pdf-${tpl.id}`}
                                            url={() => inventoryFormTemplatesAPI.pdfUrl(tpl.id)} />
                                        {canManage && (
                                            <>
                                                <Button size="sm" variant="outline" onClick={() => openEdit(tpl)} title="Modifier" data-testid={`edit-template-${tpl.id}`}>
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => duplicate(tpl)} title="Dupliquer" data-testid={`duplicate-template-${tpl.id}`}>
                                                    <Copy className="w-4 h-4" />
                                                </Button>
                                                <Button size="sm" variant="outline" className="text-red-500 hover:text-red-700" onClick={() => setDeleteTarget(tpl)} title="Supprimer" data-testid={`delete-template-${tpl.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-5xl sm:w-full sm:h-[92vh] overflow-hidden flex flex-col">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle>{form.id ? 'Modifier le modèle' : 'Nouveau modèle de fiche'}</DialogTitle>
                        <DialogDescription>
                            Sélectionnez les produits industriels et les produits fournisseurs à inclure.
                        </DialogDescription>
                    </DialogHeader>

                    {/* Top config */}
                    <div className="flex-shrink-0 grid grid-cols-1 sm:grid-cols-3 gap-3 py-3 border-b">
                        <div className="space-y-1 sm:col-span-1">
                            <Label className="text-xs">Nom du modèle *</Label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Inventaire Réserve Siège" data-testid="template-name-input" />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Site (optionnel)</Label>
                            <Select value={form.site_id || '__none__'} onValueChange={(v) => setForm({ ...form, site_id: v === '__none__' ? '' : v })}>
                                <SelectTrigger data-testid="template-site-select"><SelectValue placeholder="Aucun" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">Aucun</SelectItem>
                                    {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs">Regroupement</Label>
                            <Select value={form.group_mode} onValueChange={(v) => setForm({ ...form, group_mode: v })}>
                                <SelectTrigger data-testid="template-group-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="category">Catégorie</SelectItem>
                                    <SelectItem value="category_family">Catégorie → Famille</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Zones */}
                    <div className="flex-shrink-0 flex flex-wrap items-center gap-3 py-2 border-b">
                        <Label className="text-xs">Zones :</Label>
                        {DEFAULT_ZONES.map(z => {
                            const checked = form.zones.includes(z);
                            return (
                                <label key={z} className={`flex items-center gap-1.5 px-2 py-1 rounded border cursor-pointer text-xs ${checked ? 'bg-amber-50 border-amber-300 text-amber-700' : 'bg-white border-slate-200 text-slate-600'}`}>
                                    <Checkbox
                                        checked={checked}
                                        onCheckedChange={(c) => setForm(prev => ({
                                            ...prev,
                                            zones: c ? [...prev.zones, z] : prev.zones.filter(x => x !== z),
                                        }))}
                                        data-testid={`zone-${z.toLowerCase()}`}
                                    />
                                    {z}
                                </label>
                            );
                        })}
                    </div>

                    {/* Picker filters */}
                    <div className="flex-shrink-0 grid grid-cols-2 sm:grid-cols-5 gap-2 py-2 border-b">
                        <div className="sm:col-span-2 relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                className="pl-8 h-9"
                                placeholder="Rechercher un produit..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                data-testid="picker-search-input"
                            />
                        </div>
                        <Select value={filterCategory} onValueChange={setFilterCategory}>
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Toutes catégories</SelectItem>
                                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={filterFamille} onValueChange={setFilterFamille}>
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Toutes familles</SelectItem>
                                {familles.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={filterSupplier} onValueChange={setFilterSupplier}>
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous fournisseurs</SelectItem>
                                {suppliersList.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex-shrink-0 flex items-center justify-between py-1.5 text-xs">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                            <Checkbox checked={onlySelected} onCheckedChange={setOnlySelected} data-testid="only-selected-checkbox" />
                            Afficher uniquement les sélectionnés
                        </label>
                        <span className="text-slate-500">
                            <strong className="text-slate-900">{form.items.length}</strong> produits industriels — <strong className="text-emerald-600">{totalSelectedSp}</strong> fournisseurs
                        </span>
                    </div>

                    {/* Picker tree: Category > Family > Product > Supplier */}
                    <div className="flex-1 overflow-auto min-h-0 border rounded">
                        {groupedTree.length === 0 ? (
                            <div className="p-8 text-center text-slate-400 text-sm">Aucun produit correspondant aux filtres</div>
                        ) : (
                            <div className="divide-y">
                                {groupedTree.map(({ cat, families }) => {
                                    const catKey = `cat::${cat}`;
                                    const catOpen = expanded[catKey];
                                    const allCatProds = families.flatMap(f => f.prods);
                                    const catState = catSelectionState(allCatProds);
                                    return (
                                        <div key={cat}>
                                            {/* CATEGORY ROW */}
                                            <div
                                                className={`flex items-center gap-2 px-2 py-2 bg-slate-100 ${catState !== 'none' ? 'border-l-4 border-amber-500' : ''}`}
                                            >
                                                <Checkbox
                                                    checked={catState === 'all' ? true : (catState === 'partial' ? 'indeterminate' : false)}
                                                    onCheckedChange={(c) => toggleMultiple(allCatProds, !!c)}
                                                    data-testid={`cat-checkbox-${cat}`}
                                                />
                                                <button
                                                    type="button"
                                                    className="flex items-center gap-1 flex-1 text-left"
                                                    onClick={() => setExpanded(e => ({ ...e, [catKey]: !e[catKey] }))}
                                                >
                                                    {catOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                                                    <span className="font-bold text-slate-900 uppercase text-xs tracking-wide">{cat}</span>
                                                    <span className="ml-2 text-xs text-slate-500">
                                                        {allCatProds.filter(p => getItem(p.id)).length}/{allCatProds.length} produits
                                                    </span>
                                                </button>
                                            </div>
                                            {catOpen && families.map(({ fam, prods }) => {
                                                const famKey = `fam::${cat}::${fam}`;
                                                const famOpen = expanded[famKey];
                                                const famState = catSelectionState(prods);
                                                return (
                                                    <div key={fam} className="ml-4">
                                                        {/* FAMILY ROW */}
                                                        <div
                                                            className={`flex items-center gap-2 px-2 py-1.5 bg-slate-50 ${famState !== 'none' ? 'border-l-4 border-amber-400' : ''}`}
                                                        >
                                                            <Checkbox
                                                                checked={famState === 'all' ? true : (famState === 'partial' ? 'indeterminate' : false)}
                                                                onCheckedChange={(c) => toggleMultiple(prods, !!c)}
                                                                data-testid={`fam-checkbox-${cat}-${fam}`}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="flex items-center gap-1 flex-1 text-left"
                                                                onClick={() => setExpanded(e => ({ ...e, [famKey]: !e[famKey] }))}
                                                            >
                                                                {famOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                                                                <span className="font-semibold text-slate-700 text-xs">{fam}</span>
                                                                <span className="ml-2 text-xs text-slate-400">
                                                                    {prods.filter(p => getItem(p.id)).length}/{prods.length}
                                                                </span>
                                                            </button>
                                                        </div>
                                                        {famOpen && prods.map(ip => {
                                                            const item = getItem(ip.id);
                                                            const isSelected = !!item;
                                                            const sps = spByIndustrial.get(ip.id) || [];
                                                            const ipKey = `ip::${ip.id}`;
                                                            const ipOpen = expanded[ipKey];
                                                            return (
                                                                <div key={ip.id} className="ml-4">
                                                                    {/* PRODUCT ROW */}
                                                                    <div className={`flex items-center gap-2 px-2 py-1.5 text-sm ${isSelected ? 'bg-amber-50' : 'hover:bg-slate-50'}`}>
                                                                        <Checkbox
                                                                            checked={isSelected}
                                                                            onCheckedChange={() => toggleIndustrial(ip)}
                                                                            data-testid={`ip-checkbox-${ip.id}`}
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            className="flex items-center gap-1 flex-1 text-left"
                                                                            onClick={() => setExpanded(e => ({ ...e, [ipKey]: !e[ipKey] }))}
                                                                        >
                                                                            {sps.length > 0 && (ipOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />)}
                                                                            {sps.length === 0 && <span className="w-4" />}
                                                                            <span className="font-medium text-slate-800 truncate">{ip.name}</span>
                                                                            {sps.length > 0 && (
                                                                                <span className="ml-auto text-xs text-slate-400">
                                                                                    {item?.supplier_product_ids?.length || 0}/{sps.length}
                                                                                </span>
                                                                            )}
                                                                        </button>
                                                                    </div>
                                                                    {/* SUPPLIER PRODUCTS */}
                                                                    {ipOpen && sps.length > 0 && (
                                                                        <div className="bg-slate-50/50 ml-4">
                                                                            {sps.map(sp => {
                                                                                const checked = item?.supplier_product_ids?.includes(sp.id) || false;
                                                                                return (
                                                                                    <label
                                                                                        key={sp.id}
                                                                                        className={`flex items-center gap-2 pl-8 pr-3 py-1.5 text-xs cursor-pointer ${checked ? 'bg-emerald-50' : 'hover:bg-slate-100'}`}
                                                                                        data-testid={`sp-checkbox-label-${sp.id}`}
                                                                                    >
                                                                                        <Checkbox
                                                                                            checked={checked}
                                                                                            onCheckedChange={() => toggleSupplier(ip.id, sp.id)}
                                                                                            data-testid={`sp-checkbox-${sp.id}`}
                                                                                        />
                                                                                        <span className="font-medium text-slate-700">{sp.supplier_name_product || sp.supplier_ref || 'Sans libellé'}</span>
                                                                                        <span className="text-slate-400">— {sp.supplier_name}</span>
                                                                                        {sp.brand && <span className="text-slate-400 italic">· {sp.brand}</span>}
                                                                                    </label>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <DialogFooter className="flex-shrink-0 pt-3 border-t">
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                        <Button onClick={save} className="btn-primary" data-testid="save-template-btn">
                            {form.id ? 'Enregistrer' : 'Créer le modèle'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer ce modèle ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Cette action est irréversible. Le modèle <strong>{deleteTarget?.name}</strong> sera définitivement supprimé.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-template-btn">Supprimer</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────────────
// FromMovementsCard
// Génération de fiches d'inventaire vierges listant UNIQUEMENT les produits
// ayant eu un mouvement (dernier inventaire validé + réceptions du mois ciblé).
// Sortie : PDF avec deux colonnes de saisie « Unité d'achat » et « Pièce ».
// ─────────────────────────────────────────────────────────────────────────────
function FromMovementsCard({ sites }) {
    // Default to last month (typical "end-of-month inventory" use case)
    const defaultPeriod = (() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    })();
    const [period, setPeriod] = React.useState(defaultPeriod);

    // Only Fuji Express sites are eligible for this workflow (per user spec).
    const feSites = React.useMemo(
        () => (sites || [])
            .filter(s => (s.name || '').startsWith('FE '))
            .sort((a, b) => {
                const order = ['FE Génipa', 'FE Cluny', 'FE Dillon', 'FE Océanis', 'FE Vauclin', 'FE Siège'];
                const ai = order.indexOf(a.name);
                const bi = order.indexOf(b.name);
                if (ai === -1 && bi === -1) return a.name.localeCompare(b.name);
                if (ai === -1) return 1;
                if (bi === -1) return -1;
                return ai - bi;
            }),
        [sites]
    );

    const generateFor = (site) => {
        const url = sitesAPI.getInventoryFormFromMovementsPdfUrl(site.id, period);
        openAuthPdf(url, 'Erreur lors de la génération du PDF');
    };

    if (feSites.length === 0) return null;

    return (
        <Card className="border-amber-200 bg-amber-50/40" data-testid="from-movements-card">
            <CardContent className="p-4 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div>
                        <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-amber-600" />
                            Fiches à partir des mouvements
                        </h2>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Liste uniquement les produits ayant eu un mouvement (dernier inventaire validé + réceptions du mois).
                            Deux colonnes de saisie : <span className="font-medium">Unité d'achat</span> et <span className="font-medium">Pièce</span>. Aucune quantité pré-remplie.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <Label htmlFor="from-mvt-period" className="text-xs text-slate-600">Mois :</Label>
                        <Input
                            id="from-mvt-period"
                            type="month"
                            value={period}
                            onChange={(e) => setPeriod(e.target.value)}
                            className="h-8 w-36"
                            data-testid="from-mvt-period-input"
                        />
                    </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                    {feSites.map(s => (
                        <div key={s.id} className="flex gap-1">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => generateFor(s)}
                                className="flex-1 min-w-0 bg-white border-amber-300 hover:bg-amber-100 text-slate-700 flex items-center justify-center gap-1.5"
                                data-testid={`from-mvt-pdf-${s.id}`}
                                title={`Générer PDF pour ${s.name} — ${period}`}
                            >
                                <Download className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                                <span className="text-xs truncate">{s.name.replace('FE ', '')}</span>
                            </Button>
                            <PrintButton className="px-2 bg-white border-amber-300 hover:bg-amber-100 text-amber-600"
                                iconClass="w-3.5 h-3.5" testId={`from-mvt-print-${s.id}`}
                                url={() => sitesAPI.getInventoryFormFromMovementsPdfUrl(s.id, period)} />
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}

